import React, { useState } from 'react';
import { Mail, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { track } from '../lib/analytics';

/**
 * Passwordless email sign-in/sign-up.
 *
 * POSTs the entered email to `/api/auth/magic-link/request`. The server always
 * responds with a generic success (enumeration protection), so the UI always
 * shows the same "check your inbox" state — regardless of whether the email
 * exists, is rate-limited, or even if SMTP is misconfigured server-side. Any
 * client-side error (network, validation) shows inline.
 */
export default function EmailMagicLinkForm({ source = 'login', next, ctaLabel = 'Send me a sign-in link' }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  // Basic client-side guard. Server still validates.
  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!isValidEmail(email)) {
      setErr('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    track('magic_link_request', { source, has_next: !!next });
    try {
      await api.post('/auth/magic-link/request', {
        email: email.trim(),
        next: next || undefined,
      });
      setSent(true);
    } catch (e2) {
      // FastAPI errors → string; anything weirder gets stringified.
      const detail = e2?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (e2?.message || 'Something went wrong. Please try again.');
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div
        className="border border-[#d8b34a]/30 bg-[#13110a] rounded-md p-5 text-sm text-zinc-200 flex items-start gap-3"
        data-testid="magic-link-sent"
      >
        <CheckCircle2 className="gold-text shrink-0 mt-0.5" size={18} />
        <div>
          <p className="font-medium text-white">Check your inbox.</p>
          <p className="mt-1 text-xs text-zinc-400 leading-relaxed">
            If <span className="text-zinc-200">{email}</span> is a valid address, we just sent a one-time
            sign-in link there. It expires in 30 minutes. Don&apos;t see it? Check spam, or{' '}
            <button
              type="button"
              onClick={() => { setSent(false); setEmail(''); }}
              className="underline gold-text hover:opacity-80"
              data-testid="magic-link-try-another"
            >
              try another email
            </button>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="magic-link-form">
      <label className="block">
        <span className="text-[10px] tracking-[0.2em] text-zinc-500 font-mono uppercase">Your email</span>
        <div className="mt-2 flex items-center gap-2 border border-white/10 rounded-sm bg-[#0a0a0b] focus-within:border-[#d8b34a]/50 transition-colors">
          <Mail size={14} className="text-zinc-500 ml-3 shrink-0" />
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="magic-link-email-input"
            className="flex-1 bg-transparent py-3 pr-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none"
          />
        </div>
      </label>

      {err && (
        <p className="text-xs text-red-300" data-testid="magic-link-error">{err}</p>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid="magic-link-submit-btn"
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#d8b34a]/40 bg-[#13110a] hover:bg-[#1a1608] text-[#e9c97d] font-semibold text-sm rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Sending…' : ctaLabel}
      </button>
      <p className="text-[10px] text-zinc-500 leading-relaxed">
        No password required — we&apos;ll email you a one-time link valid for 30 minutes.
      </p>
    </form>
  );
}
